// ProProfilePage — Fiche détaillée d'un professionnel (Services / Bien-être) :
// prestations + tarifs, réservation directe, avis clients avec notation.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronLeft, MapPin, Star, BadgeCheck, Clock, Phone, CalendarDays, X, CheckCircle2,
} from "lucide-react";
import { PROS_REGISTRY } from "@/verticals/verticalData";
import "@/verticals/vertical.css";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const SLOTS = ["08:00", "09:00", "10:00", "11:00", "14:00", "15:00", "16:00", "17:00", "18:00"];
const fmt = (n) => `${Number(n || 0).toLocaleString("fr-FR").replace(/\u202F|\u00A0/g, " ")} FCFA`;
const today = () => new Date().toISOString().slice(0, 10);

function Stars({ value, size = 13, onPick, testId }) {
  return (
    <span className="pp-stars" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i} size={size}
          color="#F5C445" fill={i <= value ? "#F5C445" : "none"}
          style={onPick ? { cursor: "pointer" } : undefined}
          onClick={onPick ? () => onPick(i) : undefined}
          data-testid={onPick ? `pp-star-${i}` : undefined}
        />
      ))}
    </span>
  );
}

export default function ProProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const pro = PROS_REGISTRY[id];
  const [reviews, setReviews] = useState({ items: [], count: 0, avg: null });
  const [booking, setBooking] = useState(null); // { service } | null
  const [confirmed, setConfirmed] = useState(null);
  const [date, setDate] = useState(today());
  const [slot, setSlot] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState("");

  const loadReviews = useMemo(() => () => {
    axios.get(`${API}/v3/pros/${id}/reviews`).then((r) => { if (r.data?.ok) setReviews(r.data); }).catch(() => {});
  }, [id]);

  useEffect(() => {
    document.title = `AYA — ${pro?.name || "Professionnel"}`;
    window.scrollTo(0, 0);
    loadReviews();
  }, [loadReviews, pro]);

  if (!pro) {
    return (
      <div className="vt-page pp-page" data-testid="pro-profile-notfound" style={{ padding: 24 }}>
        <p style={{ color: "#ECF5EE" }}>Professionnel introuvable.</p>
        <button className="vt-pillbtn active" onClick={() => navigate("/services")}>Retour aux services</button>
      </div>
    );
  }

  const displayAvg = reviews.avg != null ? String(reviews.avg).replace(".", ",") : pro.rating;
  const displayCount = Number(pro.reviews.replace(/\s/g, "")) + (reviews.count || 0);

  const submitBooking = async () => {
    if (!slot) { toast.error("Choisis un créneau horaire"); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/v3/pros/${id}/bookings`, {
        service: booking.service.name, price_fcfa: booking.service.price_fcfa, date, time: slot, note,
      });
      if (r.data?.ok) { setConfirmed(r.data.booking); setBooking(null); setNote(""); setSlot(""); }
      else toast.error("Réservation impossible, réessaie.");
    } catch (_) { toast.error("Réservation impossible, réessaie."); }
    setSending(false);
  };

  const submitReview = async () => {
    if (!myRating) { toast.error("Choisis une note (1 à 5 étoiles)"); return; }
    try {
      const r = await axios.post(`${API}/v3/pros/${id}/reviews`, { rating: myRating, comment: myComment });
      if (r.data?.ok) { toast.success("Merci pour ton avis ⭐"); setMyRating(0); setMyComment(""); loadReviews(); }
    } catch (_) { toast.error("Envoi impossible, réessaie."); }
  };

  return (
    <div className="vt-page pp-page" data-testid={`pro-profile-${id}`}>
      {/* PHOTO + HEADER */}
      <div className="pp-hero">
        <img src={pro.img} alt={pro.name} />
        <button className="vt-back pp-back" data-testid="pp-back-btn" aria-label="Retour" onClick={() => navigate(pro.back)}>
          <ChevronLeft size={18} />
        </button>
        <span className="pp-universe">{pro.universeLabel}</span>
      </div>

      <div className="vt-container pp-body">
        {/* IDENTITÉ */}
        <div className="pp-ident" data-testid="pp-identity">
          <h1>{pro.name} {pro.verified && <BadgeCheck size={18} color="#2EE27B" />}</h1>
          <div className="pp-meta">
            <Stars value={Math.round(parseFloat(displayAvg.replace(",", ".")))} />
            <strong data-testid="pp-rating">{displayAvg}</strong>
            <em>({displayCount} avis)</em>
          </div>
          <div className="pp-loc"><MapPin size={12} /> {pro.area} · {pro.distance} · <Clock size={12} /> {pro.hours}</div>
          <div className="tags pp-tags">{pro.tags.map((t) => <i key={t}>{t}</i>)}</div>
          <p className="pp-desc">{pro.desc}</p>
          <div className="pp-availrow">
            {pro.availability === "now"
              ? <span className="dispo now"><span className="dot" /> Disponible maintenant</span>
              : <span className="dispo resa"><Clock size={11} /> Sur réservation</span>}
            <button className="call" aria-label="Appeler" data-testid="pp-call-btn" onClick={() => toast.info("Mise en relation téléphonique — bientôt disponible.")}>
              <Phone size={13} color="#fff" fill="#fff" />
            </button>
          </div>
        </div>

        {/* PRESTATIONS & RÉSERVATION */}
        <h2 className="pp-h2"><CalendarDays size={15} color="#F97316" /> Prestations & tarifs</h2>
        <div className="pp-services" data-testid="pp-services">
          {pro.services.map((s) => (
            <div className="pp-service" key={s.name} data-testid={`pp-service-${pro.services.indexOf(s)}`}>
              <div className="inf">
                <h3>{s.name}</h3>
                <span>{s.duration}</span>
              </div>
              <strong>{fmt(s.price_fcfa)}</strong>
              <button className="book" data-testid={`pp-book-${pro.services.indexOf(s)}`} onClick={() => { setConfirmed(null); setBooking({ service: s }); }}>
                Réserver
              </button>
            </div>
          ))}
        </div>

        {/* CONFIRMATION */}
        {confirmed && (
          <div className="pp-confirm" data-testid="pp-booking-confirmed">
            <CheckCircle2 size={20} color="#2EE27B" />
            <div>
              <h3>Réservation confirmée ✅</h3>
              <p>{confirmed.service} · {confirmed.date} à {confirmed.time}<br />Réf : <strong>{confirmed.booking_id}</strong> — {pro.name} te contactera pour confirmer l'adresse.</p>
            </div>
          </div>
        )}

        {/* AVIS CLIENTS */}
        <h2 className="pp-h2"><Star size={15} color="#F5C445" fill="#F5C445" /> Avis clients</h2>
        <div className="pp-reviewform" data-testid="pp-review-form">
          <p>Tu as fait appel à {pro.name} ? Note ton expérience :</p>
          <Stars value={myRating} size={22} onPick={setMyRating} testId="pp-rating-picker" />
          <textarea
            data-testid="pp-review-comment"
            placeholder="Partage ton expérience (optionnel)…"
            value={myComment}
            onChange={(e) => setMyComment(e.target.value)}
            rows={2}
          />
          <button className="send" data-testid="pp-review-submit" onClick={submitReview}>Publier mon avis</button>
        </div>
        <div className="pp-reviews" data-testid="pp-reviews-list">
          {reviews.items.length === 0 && <p className="pp-noreviews">Aucun avis publié pour l'instant — sois le premier !</p>}
          {reviews.items.map((r) => (
            <div className="pp-review" key={r.review_id}>
              <div className="head">
                <strong>{r.author}</strong>
                <Stars value={r.rating} size={11} />
              </div>
              {r.comment && <p>{r.comment}</p>}
              <span className="date">{String(r.created_at).slice(0, 10)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* SHEET RÉSERVATION */}
      {booking && (
        <div className="pp-sheet-backdrop" data-testid="pp-booking-sheet" onClick={() => setBooking(null)}>
          <div className="pp-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="top">
              <h3>Réserver · {booking.service.name}</h3>
              <button className="x" data-testid="pp-sheet-close" aria-label="Fermer" onClick={() => setBooking(null)}><X size={16} /></button>
            </div>
            <p className="price">{fmt(booking.service.price_fcfa)} · {booking.service.duration} · {pro.name}</p>
            <label className="lab">Date</label>
            <input type="date" data-testid="pp-booking-date" min={today()} value={date} onChange={(e) => setDate(e.target.value)} />
            <label className="lab">Créneau</label>
            <div className="slots">
              {SLOTS.map((s) => (
                <button key={s} className={`slot${slot === s ? " on" : ""}`} data-testid={`pp-slot-${s.replace(":", "")}`} onClick={() => setSlot(s)}>{s}</button>
              ))}
            </div>
            <label className="lab">Précisions (adresse, détails…)</label>
            <textarea data-testid="pp-booking-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex : Cocody Riviera 3, villa 12…" />
            <button className="confirm" data-testid="pp-booking-submit" disabled={sending} onClick={submitBooking}>
              {sending ? "Réservation…" : `Confirmer la réservation · ${fmt(booking.service.price_fcfa)}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
